// Cables de conexión: curva suave entre dos pines con conectores tipo Dupont en los extremos.
import * as THREE from 'three';
import { mat } from './kit.js';

export const WIRE_COLORS = [
  { id: 'green', name: 'Verde', hex: '#25a24a' },
  { id: 'red', name: 'Rojo', hex: '#d8392f' },
  { id: 'black', name: 'Negro', hex: '#222326' },
  { id: 'blue', name: 'Azul', hex: '#2c63d8' },
  { id: 'yellow', name: 'Amarillo', hex: '#e7c21c' },
  { id: 'orange', name: 'Naranja', hex: '#ee7c19' },
  { id: 'white', name: 'Blanco', hex: '#e6e6e3' },
  { id: 'purple', name: 'Morado', hex: '#8b4ad8' },
  { id: 'gray', name: 'Gris', hex: '#8b9097' },
  { id: 'brown', name: 'Café', hex: '#7b4a27' },
];

export function wireHex(color) {
  const c = WIRE_COLORS.find((w) => w.id === color);
  return c ? c.hex : color && color.startsWith('#') ? color : '#25a24a';
}

const HOUSING = 6.5;
const housingGeo = new THREE.BoxGeometry(2.3, HOUSING, 2.3);
housingGeo.translate(0, HOUSING / 2 + 0.3, 0);
const tipGeo = new THREE.CylinderGeometry(0.62, 0.95, 1.4, 10);
tipGeo.translate(0, HOUSING + 0.3 + 0.7, 0);
const UP = new THREE.Vector3(0, 1, 0);

export function wireCurve(a, aDir, b, bDir) {
  const A = new THREE.Vector3(...a);
  const B = new THREE.Vector3(...b);
  const da = new THREE.Vector3(...aDir).normalize();
  const db = new THREE.Vector3(...bDir).normalize();
  const lift = HOUSING + 1.7;
  const a1 = A.clone().addScaledVector(da, lift);
  const b1 = B.clone().addScaledVector(db, lift);
  const dist = a1.distanceTo(b1);
  const h = Math.min(48, Math.max(5, dist * 0.26));
  const a2 = a1.clone().addScaledVector(da, Math.min(6, 2 + dist * 0.05));
  const b2 = b1.clone().addScaledVector(db, Math.min(6, 2 + dist * 0.05));
  const top = Math.max(a2.y, b2.y);
  const m1 = a2.clone().lerp(b2, 0.3);
  const m2 = a2.clone().lerp(b2, 0.7);
  m1.y = Math.max(m1.y, top) + h * 0.55;
  m2.y = Math.max(m2.y, top) + h * 0.55;
  const pts = dist < 8 ? [a1, a2, b2, b1] : [a1, a2, m1, m2, b2, b1];
  return new THREE.CatmullRomCurve3(pts, false, 'centripetal', 0.5);
}

/** Malla del cable. a, b: [x,y,z]; dirs: dirección de salida del pin (por omisión, hacia arriba). */
export function buildWire(a, aDir, b, bDir, color) {
  const group = new THREE.Group();
  const curve = wireCurve(a, aDir, b, bDir);
  const len = curve.getLength();
  const material = new THREE.MeshStandardMaterial({ color: wireHex(color), roughness: 0.42, metalness: 0.02, emissive: '#000000' });
  const tube = new THREE.Mesh(new THREE.TubeGeometry(curve, Math.max(12, Math.min(160, Math.round(len / 2.2))), 0.55, 8, false), material);
  tube.castShadow = true;
  group.add(tube);
  const housingMat = mat('#16171a', { roughness: 0.5 });
  for (const [p, d] of [[a, aDir], [b, bDir]]) {
    const dir = new THREE.Vector3(...d).normalize();
    const q = new THREE.Quaternion().setFromUnitVectors(UP, dir);
    const h = new THREE.Mesh(housingGeo, housingMat);
    h.position.set(...p);
    h.quaternion.copy(q);
    h.castShadow = true;
    const tip = new THREE.Mesh(tipGeo, material);
    tip.position.copy(h.position);
    tip.quaternion.copy(q);
    group.add(h, tip);
  }
  group.userData.material = material;
  return group;
}

export function setWireHighlight(group, level) {
  const m = group.userData.material;
  if (!m) return;
  m.emissive.set(level === 2 ? '#2fd3e3' : level === 1 ? '#1b6f78' : '#000000');
  m.emissiveIntensity = level ? 0.55 : 0;
}

export function disposeWire(group) {
  group.traverse((o) => {
    if (o.geometry && o.geometry !== housingGeo && o.geometry !== tipGeo) o.geometry.dispose();
  });
  if (group.userData.material) group.userData.material.dispose();
}
